/**
 * Pick the case an entry is for: the firm's cases by client name, employer
 * or visa type, searched on the server.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ChevronsUpDown } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { queryClient } from "@/lib/query/queryClient";
import { cn } from "@/lib/utils";

import { TimeAPI } from "../api/timeApi";
import { caseOptionDetail, caseTone } from "../model";
import { timeKeys } from "../queries";
import type { CaseOption } from "../types";

const useDebounced = (value: string, ms = 200) => {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setOut(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return out;
};

type Props = {
  value?: string | null;
  label?: string;
  onPick: (option: CaseOption) => void;
  disabled?: boolean;
  className?: string;
  trigger?: ReactNode;
};

export const CaseDot: FC<{ id?: string | null; className?: string }> = ({
  id,
  className
}) => (
  <span
    className={cn("size-2 shrink-0 rounded-xs", caseTone(id).dot, className)}
  />
);

export const CasePicker: FC<Props> = ({
  value,
  label,
  onPick,
  disabled,
  className,
  trigger
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const query = useDebounced(q);
  const { data = [], isFetching } = useQuery(
    {
      queryKey: [...timeKeys.admin("cases"), query],
      queryFn: () => TimeAPI.searchCases(query),
      enabled: open,
      staleTime: 60_000
    },
    queryClient
  );
  const first = data[0]?.individual_id ?? "";
  const [active, setActive] = useState(first);
  useEffect(() => setActive(first), [first]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ?? (
          <Button
            variant="secondary"
            size="sm"
            className={cn("w-64 justify-start font-normal", className)}
            onClick={(e) => e.stopPropagation()}
            data-testid="case-picker"
          >
            {value ? (
              <CaseDot id={value} />
            ) : (
              <AlertCircle className="size-3 text-warn" />
            )}
            <span className="flex-1 truncate text-left">
              {value ? label || "Case" : "Choose a case"}
            </span>
            <ChevronsUpDown className="size-3 text-fg-3" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false} value={active} onValueChange={setActive}>
          <CommandInput
            placeholder="Search cases…"
            value={q}
            onValueChange={setQ}
          />
          <CommandList>
            <CommandEmpty>
              {isFetching ? "Searching…" : "No matching cases."}
            </CommandEmpty>
            <CommandGroup>
              {data.map((option) => {
                const detail = caseOptionDetail(option, data);
                return (
                  <CommandItem
                    key={option.individual_id}
                    value={option.individual_id}
                    onSelect={() => {
                      onPick(option);
                      setOpen(false);
                    }}
                  >
                    <CaseDot id={option.individual_id} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{option.label}</span>
                      {detail ? (
                        <span className="truncate text-sm text-fg-3">
                          {detail}
                        </span>
                      ) : null}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
